import type { ActiveBet, ResolveFn, Resolution, RollResult, TableRules } from '../types.js';

export const SMALL_NUMBERS = new Set([2, 3, 4, 5, 6]);
export const TALL_NUMBERS  = new Set([8, 9, 10, 11, 12]);
export const ALL_NUMBERS   = new Set([2, 3, 4, 5, 6, 8, 9, 10, 11, 12]);

/**
 * Shared body for the three ATS resolvers. Each bet's seen-numbers set
 * lives in `bet.metadata.seen: number[]`. On every non-7 roll the set
 * grows; on a 7 the bet loses; once the set covers `required`, the bet
 * wins at `amount × payoutMul`.
 *
 * Per Q3 of the design spec: each bet starts with an empty seen set at
 * placement (no inheritance of already-rolled numbers).
 */
function resolveAtsCore(
  bet: ActiveBet,
  roll: RollResult,
  required: Set<number>,
  payoutMul: number,
): Resolution {
  if (roll.total === 7) {
    return { betId: bet.id, outcome: 'lose', payout: 0 };
  }
  const seen = new Set((bet.metadata?.seen as number[] | undefined) ?? []);
  if (required.has(roll.total)) seen.add(roll.total);
  if (seen.size === required.size) {
    return { betId: bet.id, outcome: 'win', payout: bet.amount * payoutMul };
  }
  return {
    betId: bet.id,
    outcome: 'continue',
    payout: 0,
    betMetadataPatch: { seen: [...seen] },
  };
}

// Named with the `Ats` prefix so the All-bet resolver doesn't collide with
// the unrelated `resolveAll` bet-iterator in registry.ts.
export const resolveAtsSmall: ResolveFn = (bet, roll, _state, rules: TableRules) =>
  resolveAtsCore(bet, roll, SMALL_NUMBERS, rules.atsPayouts.small);

export const resolveAtsTall: ResolveFn = (bet, roll, _state, rules: TableRules) =>
  resolveAtsCore(bet, roll, TALL_NUMBERS, rules.atsPayouts.tall);

export const resolveAtsAll: ResolveFn = (bet, roll, _state, rules: TableRules) =>
  resolveAtsCore(bet, roll, ALL_NUMBERS, rules.atsPayouts.all);
